import * as React from 'react';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { usePickerContext } from '@mui/x-date-pickers/hooks';

dayjs.extend(customParseFormat);

// The theme boxes every IconButton; keep the open icon flat inside the field.
const OPEN_BUTTON_SX = {
  border: 'none',
  backgroundColor: 'transparent',
  color: 'text.secondary',
  mr: -0.5,
  '&:hover': { backgroundColor: 'action.hover', borderColor: 'transparent' },
  '& .MuiSvgIcon-root': { fontSize: 20 },
};

interface FieldConfig {
  // First format is the display format; the rest are accepted when typing.
  formats: string[];
  placeholder: string;
  hint: string;
  invalidMessage: string;
  // Returns an error as soon as the text can no longer become valid, e.g. hour 25.
  checkPartial: (text: string) => string | null;
  icon: React.ReactNode;
}

function parse(text: string, formats: string[]): Dayjs | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const parsed = dayjs(trimmed, formats, true);
  return parsed.isValid() ? parsed : null;
}

function createTypeableField(config: FieldConfig) {
  return function TypeablePickerField() {
    const picker = usePickerContext<Dayjs | null>();
    const [text, setText] = React.useState(() => (picker.value?.isValid() ? picker.value.format(config.formats[0]) : ''));
    const [touched, setTouched] = React.useState(false);

    // Keep the text in step when a value is picked from the popup.
    React.useEffect(() => {
      const value = picker.value;
      if (value?.isValid() && !parse(text, config.formats)?.isSame(value)) {
        setText(value.format(config.formats[0]));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [picker.value]);

    const parsed = parse(text, config.formats);
    const partialError = text.trim() ? config.checkPartial(text.trim()) : null;
    const error = partialError ?? (touched && text.trim() && !parsed ? config.invalidMessage : null);

    const handleChange = (next: string) => {
      setText(next);
      picker.setValue(parse(next, config.formats));
    };

    return (
      <TextField
        ref={picker.rootRef}
        fullWidth
        label={picker.label}
        name={picker.name}
        value={text}
        placeholder={config.placeholder}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setTouched(false)}
        onBlur={() => {
          setTouched(true);
          if (parsed) setText(parsed.format(config.formats[0]));
        }}
        error={!!error}
        helperText={error ?? config.hint}
        slotProps={{
          inputLabel: { shrink: true },
          input: {
            ref: picker.triggerRef,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={`Open ${String(picker.label ?? 'picker')}`}
                  onClick={() => picker.setOpen((prev) => !prev)}
                  sx={OPEN_BUTTON_SX}
                >
                  {config.icon}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
    );
  };
}

function checkTime(text: string): string | null {
  if (/[^\d:]/.test(text)) return 'Use numbers only, 24-hour format, e.g. 14:30';
  const [hours = '', minutes] = text.includes(':') ? text.split(':') : [text.slice(0, 2), text.slice(2) || undefined];
  if (hours.length > 2 || Number(hours) > 23) return 'Hours go from 00 to 23 (24-hour format)';
  if (minutes !== undefined && (minutes.length > 2 || Number(minutes) > 59)) return 'Minutes go from 00 to 59';
  return null;
}

function checkDate(text: string): string | null {
  if (/[^\d/]/.test(text)) return 'Use DD/MM/YYYY, e.g. 30/09/2026';
  const [day = '', month, year] = text.split('/');
  if (day.length > 2 || Number(day) > 31) return 'Day goes from 01 to 31';
  if (month !== undefined && (month.length > 2 || Number(month) > 12)) return 'Month goes from 01 to 12';
  if (year !== undefined && year.length > 4) return 'Year has 4 digits, e.g. 2026';
  return null;
}

export const TypeableTimeField = createTypeableField({
  formats: ['HH:mm', 'H:mm', 'HHmm'],
  placeholder: 'HH:mm',
  hint: '24-hour, e.g. 14:30',
  invalidMessage: 'Enter time as HH:mm in 24-hour format, e.g. 14:30',
  checkPartial: checkTime,
  icon: <AccessTimeRoundedIcon />,
});

export const TypeableDateField = createTypeableField({
  formats: ['DD/MM/YYYY', 'D/M/YYYY'],
  placeholder: 'DD/MM/YYYY',
  hint: 'e.g. 30/09/2026',
  invalidMessage: 'Enter date as DD/MM/YYYY, e.g. 30/09/2026',
  checkPartial: checkDate,
  icon: <CalendarTodayRoundedIcon />,
});
